import { useEffect, useState } from 'react';

/**
 * Scrollspy for the "on this page" rail: tracks which heading currently sits
 * in the reading band (the top ~30% of the viewport, below the sticky header)
 * and returns its id. SSR/jsdom-safe — with no `IntersectionObserver` it
 * returns `undefined` and the rail simply renders without a highlight.
 */
export function useActiveHeading(ids: readonly string[]): string | undefined {
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  // Effect dependency by value, so a same-content ids array never re-subscribes.
  const idsKey = ids.join('\0');

  useEffect(() => {
    const idList = idsKey.length > 0 ? idsKey.split('\0') : [];
    if (typeof IntersectionObserver === 'undefined' || idList.length === 0) return;

    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id);
          else inBand.delete(entry.target.id);
        }
        // Highlight the first in-band heading in document order; when the band
        // is empty (reading a long passage between headings) keep the last one.
        const current = idList.find((id) => inBand.has(id));
        if (current !== undefined) setActiveId(current);
      },
      // Band: from just below the sticky header down to 30% of the viewport.
      { rootMargin: '-96px 0px -70% 0px' },
    );

    for (const id of idList) {
      const el = document.getElementById(id);
      if (el !== null) observer.observe(el);
    }
    return () => {
      observer.disconnect();
    };
  }, [idsKey]);

  return activeId;
}
